import { useState } from 'react';

const QR_CODE_URL =
  'https://storage.readdy-site.link/project_files/6f264cdb-e651-41f3-9b8c-2dc509d942b2/858e8da7-2784-4f1d-ac2f-573e9dfe9b16_L_gainfriends_2dbarcodes_BW.png?v=7cb39c0ee81480bf9e8aef99c485c7a8';

export default function Hero() {
  const [showQR, setShowQR] = useState(false);

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden bg-gradient-to-br from-emerald-50 via-stone-50 to-amber-50/60">
      {/* Soft background orbs */}
      <div className="absolute top-[-80px] left-[-80px] w-[420px] h-[420px] rounded-full bg-emerald-100/60 blur-3xl pointer-events-none"></div>
      <div className="absolute bottom-[-60px] right-[-60px] w-[340px] h-[340px] rounded-full bg-amber-100/70 blur-3xl pointer-events-none"></div>
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full bg-teal-50/40 blur-3xl pointer-events-none"></div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 pt-28 pb-16 sm:pt-36 sm:pb-24 lg:py-32 w-full">
        <div className="grid lg:grid-cols-[1fr_560px] xl:grid-cols-[1fr_640px] gap-10 lg:gap-16 items-center">

          {/* Left Content — text first on mobile */}
          <div className="space-y-5 sm:space-y-7 text-center lg:text-left order-1 lg:order-1">

            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 bg-white border border-emerald-200 rounded-full text-emerald-700 text-xs sm:text-sm font-medium shadow-sm">
              <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-emerald-500 animate-pulse inline-block flex-shrink-0"></span>
              成立超過 60 年的菁英商業社團
            </div>

            {/* Main Title */}
            <div className="space-y-2">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-black text-emerald-900 leading-[1.1] tracking-tight">
                台北市
                <br />
                青年企業研究社
              </h1>
              <p className="text-lg sm:text-xl lg:text-2xl font-light text-amber-600 tracking-widest mt-2 sm:mt-3">
                YMC Taipei
              </p>
            </div>

            {/* Description */}
            <p className="text-sm sm:text-base lg:text-lg text-gray-500 leading-relaxed max-w-lg mx-auto lg:mx-0">
              匯聚台灣<strong className="text-emerald-800 font-semibold">跨世代商業菁英</strong>，每一次聚會都是視野的拓展、人脈的深耕，與下一個突破的起點。
            </p>

            {/* Core Values */}
            <div className="flex flex-wrap gap-2 sm:gap-2.5 justify-center lg:justify-start">
              {[
                { icon: 'ri-microscope-line', label: '研究', color: 'emerald' },
                { icon: 'ri-hand-heart-line', label: '友誼', color: 'amber' },
                { icon: 'ri-service-line', label: '服務', color: 'teal' },
              ].map((v) => (
                <div
                  key={v.label}
                  className={`px-4 sm:px-5 py-1.5 sm:py-2 bg-white border border-${v.color}-200 rounded-full flex items-center gap-1.5 sm:gap-2 hover:shadow-sm transition-all duration-300 cursor-pointer`}
                >
                  <i className={`${v.icon} text-${v.color}-600 text-sm sm:text-base w-4 h-4 flex items-center justify-center`}></i>
                  <span className={`font-semibold text-${v.color}-900 text-xs sm:text-sm`}>{v.label}</span>
                </div>
              ))}
            </div>

            {/* Key Stats */}
            <div className="flex flex-wrap gap-0 divide-x divide-gray-200 justify-center lg:justify-start pt-1">
              {[
                { value: '60+', label: '年歷史' },
                { value: '30+', label: '年度活動' },
                { value: '全台', label: '連鎖網絡' },
              ].map((s, i) => (
                <div key={s.label} className={`text-center lg:text-left ${i === 0 ? 'pr-5 sm:pr-6' : 'px-5 sm:px-6'}`}>
                  <div className="text-2xl sm:text-3xl font-black text-emerald-800">{s.value}</div>
                  <div className="text-[10px] sm:text-xs text-gray-400 mt-0.5 tracking-wide">{s.label}</div>
                </div>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-2.5 sm:gap-3 justify-center lg:justify-start pt-1">
              <a
                href="https://lin.ee/pVaOePX"
                target="_blank"
                rel="noopener noreferrer"
                className="px-6 sm:px-7 py-3 sm:py-3.5 bg-emerald-800 text-white rounded-xl font-semibold flex items-center justify-center gap-2 sm:gap-2.5 hover:bg-emerald-700 transition-all duration-300 whitespace-nowrap cursor-pointer text-sm"
              >
                加入我們
                <i className="ri-arrow-right-line text-base sm:text-lg w-4 sm:w-5 h-4 sm:h-5 flex items-center justify-center"></i>
              </a>

              {/* QR Code Button */}
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowQR((v) => !v)}
                  className="w-full sm:w-auto px-6 sm:px-7 py-3 sm:py-3.5 bg-white border border-emerald-200 text-emerald-800 rounded-xl font-semibold flex items-center justify-center gap-2 sm:gap-2.5 hover:border-emerald-400 hover:bg-emerald-50 transition-all duration-300 whitespace-nowrap cursor-pointer text-sm"
                >
                  <i className="ri-qr-code-line text-base sm:text-lg w-4 sm:w-5 h-4 sm:h-5 flex items-center justify-center"></i>
                  掃碼加入
                </button>

                {/* QR Popover */}
                {showQR && (
                  <>
                    {/* Backdrop */}
                    <div
                      className="fixed inset-0 z-40"
                      onClick={() => setShowQR(false)}
                    />
                    {/* Popup card */}
                    <div className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-3 w-56 bg-white rounded-2xl border border-emerald-100 p-4 flex flex-col items-center gap-3 animate-fade-in-up">
                      {/* Arrow */}
                      <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-4 h-4 bg-white border-r border-b border-emerald-100 rotate-45"></div>

                      <p className="text-xs font-semibold text-emerald-700 tracking-wide text-center">
                        LINE 官方帳號
                      </p>
                      <div className="w-36 h-36 flex items-center justify-center rounded-xl overflow-hidden bg-white border border-stone-100 p-2">
                        <img
                          src={QR_CODE_URL}
                          alt="掃描 QR Code 加入 YMC Taipei Line 官方帳號"
                          className="w-full h-full object-contain"
                        />
                      </div>
                      <p className="text-[11px] text-gray-400 text-center leading-relaxed">
                        用手機掃描即可加入<br />YMC Taipei Line 官方帳號
                      </p>
                      <a
                        href="https://lin.ee/pVaOePX"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full py-2 bg-emerald-700 hover:bg-emerald-600 text-white rounded-lg text-xs font-bold text-center transition-colors duration-200 whitespace-nowrap cursor-pointer"
                        onClick={() => setShowQR(false)}
                      >
                        直接點擊加入
                      </a>
                    </div>
                  </>
                )}
              </div>

              <a
                href="https://www.facebook.com/YMCTaipei/?locale=zh_TW"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="px-6 sm:px-7 py-3 sm:py-3.5 bg-white border border-gray-200 text-gray-600 rounded-xl font-semibold flex items-center justify-center gap-2 sm:gap-2.5 hover:border-gray-300 hover:text-gray-900 transition-all duration-300 whitespace-nowrap cursor-pointer text-sm"
              >
                <i className="ri-facebook-fill text-[#1877F2] text-base sm:text-lg w-4 sm:w-5 h-4 sm:h-5 flex items-center justify-center"></i>
                Facebook 社群
              </a>
            </div>
          </div>

          {/* Right Image */}
          <div className="relative order-2 flex justify-center lg:justify-end mt-4 sm:mt-0">
            {/* Offset background cards — hidden on mobile to avoid overflow */}
            <div className="hidden sm:block absolute top-4 left-4 w-full h-full rounded-3xl bg-emerald-100/70 border border-emerald-200/60"></div>
            <div className="hidden sm:block absolute -bottom-3 -right-3 w-full h-full rounded-3xl bg-amber-100/50 border border-amber-200/50"></div>

            {/* Main image */}
            <div className="relative w-full max-w-xs sm:max-w-lg lg:max-w-none">
              <img
                src="https://storage.readdy-site.link/project_files/6f264cdb-e651-41f3-9b8c-2dc509d942b2/d6291656-68f3-4d30-a38e-1cb47638b1b1_LINE_ALBUM_20260325-AI-_260330_1.jpg?v=544fe9cae70ed874076e9f0192f8fb3a"
                alt="YMC Taipei 台北市青年企業研究社 團隊"
                className="relative z-10 w-full h-auto rounded-2xl sm:rounded-3xl object-contain brightness-125 contrast-105"
              />

              {/* Floating info card — hidden on small mobile */}
              <div className="hidden sm:flex absolute z-20 -bottom-4 -left-4 lg:-left-8 bg-white rounded-2xl px-3 sm:px-4 py-2.5 sm:py-3 border border-stone-100 items-center gap-2 sm:gap-3">
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-team-line text-purple-700 w-4 sm:w-5 h-4 sm:h-5 flex items-center justify-center"></i>
                </div>
                <div>
                  <div className="text-[10px] sm:text-xs text-gray-400">本屆會員</div>
                  <div className="text-xs sm:text-sm font-bold text-gray-800 whitespace-nowrap">活躍社員 50+ 位</div>
                </div>
              </div>

              {/* Top badge — hidden on small mobile */}
              <div className="hidden sm:flex absolute z-20 -top-3 -right-3 lg:-right-5 bg-amber-500 text-white rounded-xl sm:rounded-2xl px-3 sm:px-3.5 py-1.5 sm:py-2 text-xs font-bold items-center gap-1.5">
                <i className="ri-award-line w-3.5 sm:w-4 h-3.5 sm:h-4 flex items-center justify-center"></i>
                菁英社團
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}
